import React, { useState } from 'react';
import { Users, Phone, Search, BarChart3, TrendingUp, BookOpen, PlayCircle, Award, Calendar, AlertCircle } from 'lucide-react';
import api from '../services/api';

interface QuizResult {
  lessonId: number;
  lessonName: string;
  score: number;
  submittedAt: string;
}

interface QuizzesProgressData {
  totalQuizzes: number;
  solvedQuizzes: number;
  results: QuizResult[];
}

interface LessonsProgressData {
  totalVideos: number;
  watchedVideos: number;
  progress: number;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message: string | null;
}

const ParentMonitoring: React.FC = () => {
  const [parentPhone, setParentPhone] = useState('');
  const [studentPhone, setStudentPhone] = useState('');
  const [quizzesData, setQuizzesData] = useState<QuizzesProgressData | null>(null);
  const [lessonsData, setLessonsData] = useState<LessonsProgressData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const fetchStudentProgress = async () => {
    if (!parentPhone.trim() || !studentPhone.trim()) {
      setError('يجب إدخال رقم هاتف ولي الأمر ورقم هاتف الطالب');
      return;
    }

    setLoading(true);
    setError(null);
    setHasSearched(true);

    console.log('Fetching student progress for parent:', parentPhone, 'student:', studentPhone);

    let lessonsSuccess = false;
    let quizzesSuccess = false;

    // Fetch lessons data
    try {
      const lessonsResponse = await api.get(`/api/Student/GetStudentVideosProgressByParent/${parentPhone}?studentPhone=${studentPhone}`);
      console.log('Lessons Response:', lessonsResponse.data);

      const responseData = lessonsResponse.data as any;
      if (responseData && responseData.success) {
        setLessonsData(responseData.data);
        console.log('Lessons data set:', responseData.data);
        lessonsSuccess = true;
      } else {
        console.log('Lessons response not successful');
        setLessonsData({ totalVideos: 0, watchedVideos: 0, progress: 0 });
      }
    } catch (lessonsError: any) {
      console.error('Error fetching lessons data:', lessonsError);
      setLessonsData({ totalVideos: 0, watchedVideos: 0, progress: 0 });
      if (lessonsError.response?.status === 404) {
        console.log('Lessons API returned 404 - student not found');
      }
    }

    // Fetch quizzes data
    try {
      const quizzesResponse = await api.get(`/api/Student/GetStudentQuizzesProgressByParent/${parentPhone}?studentPhone=${studentPhone}`);
      console.log('Quizzes Response:', quizzesResponse.data);

      const responseData = quizzesResponse.data as any;
      if (responseData && responseData.success) {
        setQuizzesData(responseData.data);
        console.log('Quizzes data set:', responseData.data);
        quizzesSuccess = true;
      } else {
        console.log('Quizzes response not successful');
        setQuizzesData({ totalQuizzes: 0, solvedQuizzes: 0, results: [] });
      }
    } catch (quizzesError: any) {
      console.error('Error fetching quizzes data:', quizzesError);
      setQuizzesData({ totalQuizzes: 0, solvedQuizzes: 0, results: [] });
      if (quizzesError.response?.status === 404) {
        console.log('Quizzes API returned 404 - student not found');
      }
    }

    // Only set error if both APIs fail with 404 (student not found)
    if (!lessonsSuccess && !quizzesSuccess) {
      setError('لم يتم العثور على الطالب بالأرقام المدخلة. تأكد من صحة أرقام الهواتف.');
    }

    setLoading(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchStudentProgress();
  };

  const CircularProgress = ({ percentage, size = 120, strokeWidth = 8, color = '#f97316' }: {
    percentage: number;
    size?: number;
    strokeWidth?: number;
    color?: string;
  }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const safePercentage = isNaN(percentage) ? 0 : Math.max(0, Math.min(100, percentage));
    const strokeDashoffset = circumference - (safePercentage / 100) * circumference;

    return (
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="transform -rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="transparent"
            stroke="#e5e7eb"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="transparent"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-2xl font-bold text-gray-700">{Math.round(safePercentage)}%</span>
        </div>
      </div>
    );
  };

  const BarChart = ({ data }: { data: QuizResult[] }) => {
    const maxScore = Math.max(...data.map(item => item.score), 100);
    
    return (
      <div className="space-y-4">
        {data.slice(0, 6).map((item, index) => (
          <div key={item.lessonId} className="flex items-center space-x-4 space-x-reverse">
            <div className="flex-1">
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium text-gray-700 truncate">{item.lessonName}</span>
                <span className="text-sm font-bold text-orange-600">{item.score.toFixed(1)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-orange-400 to-orange-500 h-3 rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${(item.score / maxScore) * 100}%` }}
                />
              </div>
              <span className="text-xs text-gray-500 mt-1">
                {new Date(item.submittedAt).toLocaleDateString('ar-EG')}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const videoProgress = lessonsData && lessonsData.totalVideos > 0 
    ? (lessonsData.watchedVideos / lessonsData.totalVideos) * 100 
    : 0;
  const quizProgress = quizzesData && quizzesData.totalQuizzes > 0 
    ? (quizzesData.solvedQuizzes / quizzesData.totalQuizzes) * 100 
    : 0;
  const averageScore = quizzesData?.results && quizzesData.results.length > 0
    ? quizzesData.results.reduce((acc, result) => acc + result.score, 0) / quizzesData.results.length 
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 pt-20 pb-8" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Users className="w-12 h-12 text-purple-600 ml-3" />
            <h1 className="text-4xl font-bold text-gray-900">
              متابعة ولي الأمر
            </h1>
          </div>
          <p className="text-lg text-gray-600">
            تابع تقدم طفلك الأكاديمي وأداءه في الاختبارات
          </p>
        </div>

        {/* Search Form */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-purple-100">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
            أدخل بيانات الطالب
          </h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="parentPhone" className="block text-sm font-medium text-gray-700 mb-2">
                  رقم هاتف ولي الأمر
                </label>
                <div className="relative">
                  <Phone className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="tel"
                    id="parentPhone"
                    value={parentPhone}
                    onChange={(e) => setParentPhone(e.target.value)}
                    placeholder="01xxxxxxxxx"
                    className="w-full pr-12 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>
              <div>
                <label htmlFor="studentPhone" className="block text-sm font-medium text-gray-700 mb-2">
                  رقم هاتف الطالب
                </label>
                <div className="relative">
                  <Phone className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="tel"
                    id="studentPhone"
                    value={studentPhone}
                    onChange={(e) => setStudentPhone(e.target.value)}
                    placeholder="01xxxxxxxxx"
                    className="w-full pr-12 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>
            </div>
            <div className="text-center">
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white ml-2"></div>
                    جاري البحث...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <Search className="w-5 h-5 ml-2" />
                    عرض إحصائيات الطالب
                  </div>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg mb-8 flex items-center">
            <AlertCircle className="w-5 h-5 ml-3" />
            <p>{error}</p>
          </div>
        )}

        {/* Results Section */}
        {hasSearched && !loading && !error && (lessonsData || quizzesData) && (
          <>
            {/* Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {/* Video Progress Card */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">تقدم المحاضرات</h3>
                    <p className="text-sm text-gray-600">الفيديوهات المشاهدة</p>
                  </div>
                  <PlayCircle className="w-8 h-8 text-blue-500" />
                </div>
                <div className="flex items-center justify-center mb-4">
                  <CircularProgress percentage={videoProgress} color="#3b82f6" />
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-600">
                    {lessonsData?.watchedVideos || 0} من {lessonsData?.totalVideos || 0} فيديو
                  </p>
                </div>
              </div>

              {/* Quiz Progress Card */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">تقدم الاختبارات</h3>
                    <p className="text-sm text-gray-600">الاختبارات المكتملة</p>
                  </div>
                  <BookOpen className="w-8 h-8 text-green-500" />
                </div>
                <div className="flex items-center justify-center mb-4">
                  <CircularProgress percentage={quizProgress} color="#10b981" />
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-600">
                    {quizzesData?.solvedQuizzes || 0} من {quizzesData?.totalQuizzes || 0} اختبار
                  </p>
                </div>
              </div>

              {/* Average Score Card */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">متوسط الدرجات</h3>
                    <p className="text-sm text-gray-600">الأداء العام</p>
                  </div>
                  <Award className="w-8 h-8 text-purple-500" />
                </div>
                <div className="flex items-center justify-center mb-4">
                  <CircularProgress percentage={averageScore} color="#8b5cf6" />
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-600">
                    {averageScore.toFixed(1)}% متوسط عام
                  </p>
                </div>
              </div>
            </div>

            {/* Detailed Statistics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Quiz Results Chart */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-gray-900">نتائج الاختبارات الأخيرة</h3>
                  <BarChart3 className="w-6 h-6 text-purple-500" />
                </div>
                {quizzesData?.results && quizzesData.results.length > 0 ? (
                  <BarChart data={quizzesData.results} />
                ) : (
                  <div className="text-center py-8">
                    <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">لا توجد نتائج اختبارات متاحة</p>
                  </div>
                )}
              </div>

              {/* Progress Summary */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-gray-900">ملخص التقدم</h3>
                  <TrendingUp className="w-6 h-6 text-green-500" />
                </div>
                <div className="space-y-6">
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center">
                      <PlayCircle className="w-5 h-5 text-blue-500 ml-3" />
                      <span className="text-sm font-medium text-gray-900">إجمالي الفيديوهات المشاهدة</span>
                    </div>
                    <span className="text-sm font-bold text-blue-600">{lessonsData?.watchedVideos || 0}</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center">
                      <BookOpen className="w-5 h-5 text-green-500 ml-3" />
                      <span className="text-sm font-medium text-gray-900">إجمالي الاختبارات المكتملة</span>
                    </div>
                    <span className="text-sm font-bold text-green-600">{quizzesData?.solvedQuizzes || 0}</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                    <div className="flex items-center">
                      <Award className="w-5 h-5 text-purple-500 ml-3" />
                      <span className="text-sm font-medium text-gray-900">أعلى درجة</span>
                    </div>
                    <span className="text-sm font-bold text-purple-600">
                      {quizzesData?.results && quizzesData.results.length > 0 
                        ? Math.max(...quizzesData.results.map(r => r.score)).toFixed(1) + '%'
                        : '0%'}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
                    <div className="flex items-center">
                      <Calendar className="w-5 h-5 text-orange-500 ml-3" />
                      <span className="text-sm font-medium text-gray-900">آخر نشاط</span>
                    </div>
                    <span className="text-sm font-bold text-orange-600">
                      {quizzesData?.results && quizzesData.results.length > 0 
                        ? new Date(Math.max(...quizzesData.results.map(r => new Date(r.submittedAt).getTime()))).toLocaleDateString('ar-EG')
                        : 'لا يوجد'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Achievement Badges */}
            {(videoProgress >= 50 || quizProgress >= 50 || averageScore >= 80) && (
              <div className="mt-12 bg-white rounded-2xl shadow-lg p-6 border border-purple-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">الإنجازات المحققة</h3>
                <div className="flex flex-wrap justify-center gap-4">
                  {videoProgress >= 50 && (
                    <div className="flex flex-col items-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <PlayCircle className="w-8 h-8 text-blue-500 mb-2" />
                      <span className="text-sm font-medium text-blue-700">مشاهد نشط</span>
                      <span className="text-xs text-blue-600">50%+ من الفيديوهات</span>
                    </div>
                  )}
                  
                  {quizProgress >= 50 && (
                    <div className="flex flex-col items-center p-4 bg-green-50 rounded-lg border border-green-200">
                      <BookOpen className="w-8 h-8 text-green-500 mb-2" />
                      <span className="text-sm font-medium text-green-700">متقدم في الاختبارات</span>
                      <span className="text-xs text-green-600">50%+ من الاختبارات</span>
                    </div>
                  )}
                  
                  {averageScore >= 80 && (
                    <div className="flex flex-col items-center p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <Award className="w-8 h-8 text-purple-500 mb-2" />
                      <span className="text-sm font-medium text-purple-700">أداء متميز</span>
                      <span className="text-xs text-purple-600">+80% متوسط الدرجات</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* No Results Found */}
        {hasSearched && !loading && !error && !lessonsData && !quizzesData && (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-purple-100">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">لا توجد بيانات متاحة</h3>
            <p className="text-gray-600">
              تأكد من صحة أرقام الهواتف المدخلة أو تواصل مع الإدارة
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentMonitoring;
